import React, { useState, forwardRef, useMemo, memo } from 'react';
import PropTypes from 'prop-types';

const NeumorphicInputOtp = forwardRef(({
  type,
  id,
  name,
  className,
  placeholder,
  value,
  onChange,
  onKeyDown,
  onPaste,
  maxLength,
  autoFocus,
  disabled
}, ref) => {
  // Generate unique IDs for filters using useMemo
  const filterIds = useMemo(() => ({
    filterId: `filter_${id || Math.random().toString(36).substr(2, 9)}`,
    hoverGradient1Id: `hoverGradient1_${id || Math.random().toString(36).substr(2, 9)}`,
    hoverGradient2Id: `hoverGradient2_${id || Math.random().toString(36).substr(2, 9)}`,
    hoverGradient3Id: `hoverGradient3_${id || Math.random().toString(36).substr(2, 9)}`,
    hoverGradient4Id: `hoverGradient4_${id || Math.random().toString(36).substr(2, 9)}`
  }), [id]);

  const { filterId, hoverGradient1Id, hoverGradient2Id, hoverGradient3Id, hoverGradient4Id } = filterIds;

  // Add states to track focus and hover
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className="otp-input-container" 
      style={{ position: 'relative', width: '64px', height: '83px' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* CSS Animation for smooth gradient blending */}
      {isHovered && !isFocused && (
        <style>
          {`
            @keyframes gradientBlend1 {
              0%, 100% { opacity: 0.8; }
              25%, 75% { opacity: 0; }
              50% { opacity: 0.3; }
            }
            
            @keyframes gradientBlend2 {
              0%, 50%, 100% { opacity: 0.3; }
              25% { opacity: 0.8; }
              75% { opacity: 0; }
            }
            
            @keyframes gradientBlend3 {
              0%, 100% { opacity: 0; }
              25% { opacity: 0.3; }
              50% { opacity: 0.8; }
              75% { opacity: 0.3; }
            }
            
            @keyframes gradientBlend4 {
              0% { opacity: 0.3; }
              25% { opacity: 0; }
              50%, 100% { opacity: 0.3; }
              75% { opacity: 0.8; }
            }
            
            .gradient-1 {
              animation: gradientBlend1 4s infinite ease-in-out;
            }
            
            .gradient-2 {
              animation: gradientBlend2 4s infinite ease-in-out;
            }
            
            .gradient-3 {
              animation: gradientBlend3 4s infinite ease-in-out;
            }
            
            .gradient-4 {
              animation: gradientBlend4 4s infinite ease-in-out;
            }
          `}
        </style>
      )}

      {/* Hover effect SVGs - only shown when hovered but not focused */}
      {isHovered && !isFocused && (
        <>
          <svg 
            style={{ 
              position: 'absolute', 
              width: '108%',
              height: '84%',
              top: '-2px', 
              left: '-3px',
              zIndex: 0,
              opacity: 1
            }}
            className="gradient-1"
            viewBox="0 0 64 83" 
            fill="none"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M12.5556 77.5052L3.67287 62.1622C3.2321 61.4009 3 60.5368 3 59.657L3.00001 7.99999C3.00001 5.23857 5.23859 3 8.00001 3L56 3C58.7614 3 61 5.23858 61 8L61 75C61 77.7614 58.7614 80 56 80L16.8828 80C15.0986 80 13.4496 79.0493 12.5556 77.5052Z" stroke={`url(#${hoverGradient1Id})`} strokeWidth="3"/>
            <defs>
              <linearGradient id={hoverGradient1Id} x1="32" y1="80" x2="32" y2="3" gradientUnits="userSpaceOnUse">
                <stop stopColor="#C000FF"/>
                <stop offset="0.02" stopColor="#3C014F"/>
                <stop offset="0.495" stopColor="#3C014F"/>
                <stop offset="0.95" stopColor="#3C014F"/>
                <stop offset="0.99" stopColor="#C000FF"/>
              </linearGradient>
            </defs>
          </svg>
          
          <svg 
            style={{ 
              position: 'absolute', 
              width: '108%',
              height: '84%',
              top: '-2px', 
              left: '-3px',
              zIndex: 0,
              opacity: 1
            }}
            className="gradient-2"
            viewBox="0 0 64 83" 
            fill="none"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M12.5556 77.5052L3.67287 62.1622C3.2321 61.4009 3 60.5368 3 59.657L3.00001 7.99999C3.00001 5.23857 5.23859 3 8.00001 3L56 3C58.7614 3 61 5.23858 61 8L61 75C61 77.7614 58.7614 80 56 80L16.8828 80C15.0986 80 13.4496 79.0493 12.5556 77.5052Z" stroke={`url(#${hoverGradient2Id})`} strokeWidth="3"/>
            <defs>
              <linearGradient id={hoverGradient2Id} x1="61" y1="41.5" x2="3" y2="41.5" gradientUnits="userSpaceOnUse">
                <stop stopColor="#C000FF"/>
                <stop offset="0.02" stopColor="#3C014F"/>
                <stop offset="0.495" stopColor="#3C014F"/>
                <stop offset="0.95" stopColor="#3C014F"/>
                <stop offset="0.99" stopColor="#C000FF"/>
              </linearGradient>
            </defs>
          </svg>
          
          <svg 
            style={{ 
              position: 'absolute', 
              width: '108%',
              height: '84%',
              top: '-2px', 
              left: '-3px',
              zIndex: 0,
              opacity: 1
            }}
            className="gradient-3"
            viewBox="0 0 64 83" 
            fill="none"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M12.5556 77.5052L3.67287 62.1622C3.2321 61.4009 3 60.5368 3 59.657L3.00001 7.99999C3.00001 5.23857 5.23859 3 8.00001 3L56 3C58.7614 3 61 5.23858 61 8L61 75C61 77.7614 58.7614 80 56 80L16.8828 80C15.0986 80 13.4496 79.0493 12.5556 77.5052Z" stroke={`url(#${hoverGradient3Id})`} strokeWidth="3"/>
            <defs>
              <linearGradient id={hoverGradient3Id} x1="3" y1="41.5" x2="61" y2="41.5" gradientUnits="userSpaceOnUse">
                <stop stopColor="#C000FF"/>
                <stop offset="0.02" stopColor="#3C014F"/>
                <stop offset="0.495" stopColor="#3C014F"/>
                <stop offset="0.95" stopColor="#3C014F"/>
                <stop offset="0.99" stopColor="#C000FF"/>
              </linearGradient>
            </defs>
          </svg>
          
          <svg 
            style={{ 
              position: 'absolute', 
              width: '108%',
              height: '84%',
              top: '-2px', 
              left: '-3px',
              zIndex: 0,
              opacity: 1
            }}
            className="gradient-4"
            viewBox="0 0 64 83" 
            fill="none"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M12.5556 77.5052L3.67287 62.1622C3.2321 61.4009 3 60.5368 3 59.657L3.00001 7.99999C3.00001 5.23857 5.23859 3 8.00001 3L56 3C58.7614 3 61 5.23858 61 8L61 75C61 77.7614 58.7614 80 56 80L16.8828 80C15.0986 80 13.4496 79.0493 12.5556 77.5052Z" stroke={`url(#${hoverGradient4Id})`} strokeWidth="3"/>
            <defs>
              <linearGradient id={hoverGradient4Id} x1="32" y1="3" x2="32" y2="80" gradientUnits="userSpaceOnUse">
                <stop stopColor="#C000FF"/>
                <stop offset="0.15" stopColor="#3C014F"/>
                <stop offset="0.495" stopColor="#3C014F"/>
                <stop offset="0.9899" stopColor="#3C014F"/>
                <stop offset="0.99" stopColor="#C000FF"/>
              </linearGradient>
            </defs>
          </svg>
        </>
      )}

      {/* Focus outline SVG that appears only when focused */}
      {isFocused && (
        <svg 
          style={{ 
            position: 'absolute', 
            width: '108%',
            height: '84%',
            top: '-2px', 
            left: '-3px',
            zIndex: 0
          }}
          viewBox="0 0 64 83"
          fill="none"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Add a separate glow path with no dark edge */}
          <path 
            d="M12.5556 77.5052L3.67287 62.1622C3.2321 61.4009 3 60.5368 3 59.657L3.00001 7.99999C3.00001 5.23857 5.23859 3 8.00001 3L56 3C58.7614 3 61 5.23858 61 8L61 75C61 77.7614 58.7614 80 56 80L16.8828 80C15.0986 80 13.4496 79.0493 12.5556 77.5052Z"
            stroke="#F4D3FF"
            strokeWidth="1"
            fill="none"
            filter="blur(2.3px)"
          />
          
          {/* Main border with no filter */}
          <path 
            d="M12.5556 77.5052L3.67287 62.1622C3.2321 61.4009 3 60.5368 3 59.657L3.00001 7.99999C3.00001 5.23857 5.23859 3 8.00001 3L56 3C58.7614 3 61 5.23858 61 8L61 75C61 77.7614 58.7614 80 56 80L16.8828 80C15.0986 80 13.4496 79.0493 12.5556 77.5052Z"
            stroke="#9404C3" 
            strokeWidth="3"
            fill="none"
          />
        </svg>
      )}
      
      {/* Main SVG background */}
      <svg 
        style={{ position: 'absolute', width: '98%', height: '80%', top: 0, left: 0, zIndex: 1 }}
        viewBox="0 0 64 83" 
        fill="none"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g filter={`url(#${filterId})`}>
          <path d="M12.5556 77.5052L3.67287 62.1622C3.2321 61.4009 3 60.5368 3 59.657L3.00001 7.99999C3.00001 5.23857 5.23859 3 8.00001 3L56 3C58.7614 3 61 5.23858 61 8L61 75C61 77.7614 58.7614 80 56 80L16.8828 80C15.0986 80 13.4496 79.0493 12.5556 77.5052Z" fill="#282B32"/>
        </g>
        <defs>
          <filter id={filterId} x="-7" y="-7" width="78" height="97" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-1" dy="-1"/>
            <feGaussianBlur stdDeviation="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.137255 0 0 0 0.5 0"/>
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_480_845"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="1" dy="1"/>
            <feGaussianBlur stdDeviation="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.203922 0 0 0 0 0.219608 0 0 0 0 0.254902 0 0 0 0.3 0"/>
            <feBlend mode="normal" in2="effect1_dropShadow_480_845" result="effect2_dropShadow_480_845"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_480_845" result="shape"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="10" dy="10"/>
            <feGaussianBlur stdDeviation="12.5"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.137255 0 0 0 0.9 0"/>
            <feBlend mode="normal" in2="shape" result="effect3_innerShadow_480_845"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-10" dy="-10"/>
            <feGaussianBlur stdDeviation="10"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.203922 0 0 0 0 0.219608 0 0 0 0 0.254902 0 0 0 0.9 0"/>
            <feBlend mode="normal" in2="effect3_innerShadow_480_845" result="effect4_innerShadow_480_845"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="10" dy="-10"/>
            <feGaussianBlur stdDeviation="10"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.137255 0 0 0 0.2 0"/>
            <feBlend mode="normal" in2="effect4_innerShadow_480_845" result="effect5_innerShadow_480_845"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-10" dy="10"/>
            <feGaussianBlur stdDeviation="10"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.137255 0 0 0 0.2 0"/>
            <feBlend mode="normal" in2="effect5_innerShadow_480_845" result="effect6_innerShadow_480_845"/>
          </filter>
        </defs>
      </svg>
      
      {/* Actual input field */}
      <input
        ref={ref}
        type={type}
        id={id}
        name={name}
        className={`${className} otp-input-field`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        maxLength={maxLength}
        autoFocus={autoFocus}
        disabled={disabled}
        style={{
          position: 'absolute',
          width: '98%',
          height: '80%',
          backgroundColor: 'transparent',
          color: 'white',
          fontSize: '1.5rem',
          zIndex: 2,
          border: 'none',
          outline: 'none',
          textAlign: 'center',
          paddingTop: '3px', // Adjust this to vertically center the digit
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />
    </div>
  );
});

const arePropsEqual = (prevProps, nextProps) => {
  return (
    prevProps.type === nextProps.type &&
    prevProps.id === nextProps.id &&
    prevProps.name === nextProps.name &&
    prevProps.className === nextProps.className &&
    prevProps.placeholder === nextProps.placeholder &&
    prevProps.value === nextProps.value &&
    prevProps.onChange === nextProps.onChange &&
    prevProps.onKeyDown === nextProps.onKeyDown &&
    prevProps.onPaste === nextProps.onPaste &&
    prevProps.maxLength === nextProps.maxLength &&
    prevProps.autoFocus === nextProps.autoFocus &&
    prevProps.disabled === nextProps.disabled
  );
};

NeumorphicInputOtp.propTypes = {
  type: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  onPaste: PropTypes.func,
  maxLength: PropTypes.number,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool
};

// Add display name for better debugging
NeumorphicInputOtp.displayName = 'NeumorphicInputOtp';

export default memo(NeumorphicInputOtp, arePropsEqual);